import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Factory, Play, CheckCircle2, XCircle, PackagePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FormModal, Field, SectionTitle } from "@/pages/InwardStock";

const STATUSES = ["Planned", "In Progress", "Completed", "Cancelled"];

const statusBadge = (s) => {
  if (s === "Completed") return "badge-ok";
  if (s === "Cancelled") return "badge-danger";
  if (s === "In Progress") return "bg-[#E3F0FF] text-[#145B98] border border-[#B8D6F0] rounded-full px-2 py-0.5 text-[11px] font-bold";
  return "bg-zinc-100 text-zinc-600 border border-zinc-300 rounded-full px-2 py-0.5 text-[11px] font-bold";
};

export default function Production() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [rawBatches, setRawBatches] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty());

  function empty() {
    return {
      date: new Date().toISOString().slice(0, 10),
      raw_items: [{ batch_number: "", quantity: 1 }],
      output: {
        batch_number: "",
        brand: "",
        model: "",
        battery_type: "Li-ion",
        voltage: "",
        capacity_ah: "",
        quantity: 1,
        warranty_months: 12,
        low_stock_threshold: 5,
      },
      notes: "",
    };
  }

  const load = async () => {
    const [p, r] = await Promise.all([
      api.get("/productions"),
      api.get("/stock/in-hand?stock_type=raw"),
    ]);
    setItems(p.data);
    setRawBatches(r.data.filter((b) => b.in_hand > 0));
  };
  useEffect(() => { load(); }, []);

  const close = () => { setShow(false); setForm(empty()); };

  const addRaw = () => setForm({ ...form, raw_items: [...form.raw_items, { batch_number: "", quantity: 1 }] });
  const removeRaw = (i) => setForm({ ...form, raw_items: form.raw_items.filter((_, idx) => idx !== i) });
  const updateRaw = (i, patch) => setForm({
    ...form,
    raw_items: form.raw_items.map((it, idx) => idx === i ? { ...it, ...patch } : it),
  });

  const totalRawCost = useMemo(() => {
    return form.raw_items.reduce((s, it) => {
      const b = rawBatches.find((x) => x.batch_number === it.batch_number);
      const cost = b ? (b.unit_cost || 0) : 0;
      return s + cost * (parseInt(it.quantity) || 0);
    }, 0);
  }, [form.raw_items, rawBatches]);

  const perUnitCost = form.output.quantity > 0 ? totalRawCost / parseInt(form.output.quantity || 1) : 0;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        date: form.date,
        notes: form.notes,
        raw_items: form.raw_items.map((it) => {
          const b = rawBatches.find((x) => x.batch_number === it.batch_number);
          return {
            batch_number: it.batch_number.toUpperCase(),
            quantity: parseInt(it.quantity),
            unit_cost: b ? (b.unit_cost || 0) : 0,
          };
        }),
        output: {
          ...form.output,
          batch_number: form.output.batch_number.toUpperCase(),
          quantity: parseInt(form.output.quantity),
          warranty_months: parseInt(form.output.warranty_months || 0),
          low_stock_threshold: parseInt(form.output.low_stock_threshold || 5),
        },
      };
      await api.post("/productions", payload);
      toast.success("Production started · raw stock deducted");
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const setStatus = async (p, status) => {
    if (status === "Cancelled" && !window.confirm(`Cancel ${p.production_number}? Raw materials will be restored.`)) return;
    if (status === "Completed" && !window.confirm(`Mark ${p.production_number} as Completed? Output batch ${p.output.batch_number} (${p.output.quantity} units) will be added to Ready Stock.`)) return;
    try {
      await api.patch(`/productions/${p.id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.production_number}?`)) return;
    try {
      await api.delete(`/productions/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="production-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#28B516] mr-2 align-middle" />Manufacturing</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Production</h1>
          <div className="font-mono text-xs text-zinc-500 mt-2">Consume raw materials · produce ready batteries</div>
        </div>
        <button onClick={() => { setForm(empty()); setShow(true); }} className="btn-primary flex items-center gap-2" data-testid="new-production-btn">
          <Plus className="w-4 h-4" /> New Production Run
        </button>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="data-table" data-testid="production-table">
          <thead>
            <tr>
              <th>Prod No.</th>
              <th>Date</th>
              <th>Output Batch</th>
              <th>Product</th>
              <th className="text-right">Qty</th>
              <th>Raw Items</th>
              <th className="text-right">Raw Cost ₹</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="9" className="text-center py-12 text-zinc-500">
                <Factory className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No production runs yet.
              </td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} data-testid={`prod-row-${p.id}`}>
                <td className="font-mono font-bold text-[#28B516]">{p.production_number}</td>
                <td>{p.date}</td>
                <td className="font-mono font-semibold">{p.output?.batch_number}</td>
                <td className="font-sans">{p.output?.brand} {p.output?.model}<div className="text-xs text-zinc-500">{p.output?.voltage}V · {p.output?.capacity_ah}Ah</div></td>
                <td className="text-right font-mono">{p.output?.quantity}</td>
                <td className="font-sans text-xs">
                  {(p.raw_items || []).map((it) => (
                    <div key={it.batch_number}><span className="font-mono text-[#28B516]">{it.batch_number}</span> × {it.quantity}</div>
                  ))}
                </td>
                <td className="text-right font-mono">{(p.total_raw_cost || 0).toLocaleString()}</td>
                <td><span className={statusBadge(p.status)} data-testid={`prod-status-${p.id}`}>{p.status}</span></td>
                <td>
                  <div className="flex gap-2">
                    {p.status === "Planned" && (user?.role === "admin" || p.created_by === user?.email) && (
                      <button onClick={() => setStatus(p, "In Progress")} className="text-zinc-400 hover:text-[#145B98] transition-colors" title="Start" data-testid={`start-prod-${p.id}`}>
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    {(p.status === "Planned" || p.status === "In Progress") && (user?.role === "admin" || p.created_by === user?.email) && (
                      <>
                        <button onClick={() => setStatus(p, "Completed")} className="text-zinc-400 hover:text-[#28B516] transition-colors" title="Complete" data-testid={`complete-prod-${p.id}`}>
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setStatus(p, "Cancelled")} className="text-zinc-400 hover:text-[#D9363E] transition-colors" title="Cancel & restore raw" data-testid={`cancel-prod-${p.id}`}>
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {p.status !== "Completed" && (user?.role === "admin" || p.created_by === user?.email) && (
                      <button onClick={() => remove(p)} className="text-zinc-400 hover:text-[#D9363E] transition-colors" title="Delete" data-testid={`del-prod-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <FormModal onClose={close}>
          <form onSubmit={submit} className="space-y-5" data-testid="production-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">New Production Run</h2>
              <button type="button" onClick={close} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>

            <SectionTitle>Run Details</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Production Date*">
                <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field" data-testid="prod-date" />
              </Field>
            </div>

            <SectionTitle>Raw Materials Consumed</SectionTitle>
            {rawBatches.length === 0 && (
              <div className="border border-[#D97706]/40 bg-[#FFF7EB] p-3 text-xs font-sans text-[#7A4B00]">
                No raw material batches with stock available. Add raw material via <b>Stock Inward → Raw Material</b> tab or bulk upload first.
              </div>
            )}
            <div className="space-y-3">
              {form.raw_items.map((it, i) => {
                const sel = rawBatches.find((b) => b.batch_number === it.batch_number);
                return (
                  <div key={i} className="grid grid-cols-12 gap-3 items-end" data-testid={`raw-row-${i}`}>
                    <div className="col-span-6">
                      <div className="overline mb-2">Raw Batch*</div>
                      <select
                        required
                        value={it.batch_number}
                        onChange={(e) => updateRaw(i, { batch_number: e.target.value })}
                        className="input-field w-full font-mono"
                        data-testid={`raw-batch-${i}`}
                      >
                        <option value="">Select raw batch…</option>
                        {rawBatches.map((b) => (
                          <option key={b.batch_number} value={b.batch_number}>
                            {b.batch_number} — {b.brand} {b.model} (available: {b.in_hand})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <div className="overline mb-2">Qty*</div>
                      <input
                        required
                        type="number"
                        min="1"
                        max={sel ? sel.in_hand : undefined}
                        value={it.quantity}
                        onChange={(e) => updateRaw(i, { quantity: e.target.value })}
                        className="input-field w-full font-mono"
                        data-testid={`raw-qty-${i}`}
                      />
                      {sel && (
                        <div className="text-[10px] font-mono text-zinc-500 mt-1">avail: {sel.in_hand}</div>
                      )}
                    </div>
                    <div className="col-span-2">
                      <div className="overline mb-2">Cost ₹</div>
                      <div className="input-field font-mono text-[#28B516] font-bold">
                        {sel ? (sel.unit_cost * (parseInt(it.quantity) || 0)).toLocaleString() : "—"}
                      </div>
                    </div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => removeRaw(i)}
                        disabled={form.raw_items.length === 1}
                        className="text-zinc-400 hover:text-[#D9363E] transition-colors disabled:opacity-30 disabled:cursor-not-allowed p-2"
                        data-testid={`remove-raw-${i}`}
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              <button type="button" onClick={addRaw} className="btn-ghost flex items-center gap-2 text-xs" data-testid="add-raw-btn">
                <Plus className="w-3.5 h-3.5" /> Add Raw Item
              </button>
            </div>

            <SectionTitle>Finished Battery Output</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Output Batch #*">
                <input required value={form.output.batch_number} onChange={(e) => setForm({ ...form, output: { ...form.output, batch_number: e.target.value.toUpperCase() } })} className="input-field font-mono" placeholder="BAT-2026-050" data-testid="out-batch" />
              </Field>
              <Field label="Brand"><input value={form.output.brand} onChange={(e) => setForm({ ...form, output: { ...form.output, brand: e.target.value } })} className="input-field" data-testid="out-brand" /></Field>
              <Field label="Model"><input value={form.output.model} onChange={(e) => setForm({ ...form, output: { ...form.output, model: e.target.value } })} className="input-field" data-testid="out-model" /></Field>
              <Field label="Battery Type">
                <select value={form.output.battery_type} onChange={(e) => setForm({ ...form, output: { ...form.output, battery_type: e.target.value } })} className="input-field" data-testid="out-type">
                  {["Li-ion", "Lead Acid", "AGM", "Gel", "NiCd", "Other"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Voltage (V)"><input value={form.output.voltage} onChange={(e) => setForm({ ...form, output: { ...form.output, voltage: e.target.value } })} className="input-field font-mono" data-testid="out-voltage" /></Field>
              <Field label="Capacity (Ah)"><input value={form.output.capacity_ah} onChange={(e) => setForm({ ...form, output: { ...form.output, capacity_ah: e.target.value } })} className="input-field font-mono" data-testid="out-capacity" /></Field>
              <Field label="Quantity*">
                <input required type="number" min="1" value={form.output.quantity} onChange={(e) => setForm({ ...form, output: { ...form.output, quantity: e.target.value } })} className="input-field font-mono" data-testid="out-qty" />
              </Field>
              <Field label="Warranty (Months)"><input type="number" value={form.output.warranty_months} onChange={(e) => setForm({ ...form, output: { ...form.output, warranty_months: e.target.value } })} className="input-field font-mono" data-testid="out-warranty" /></Field>
              <Field label="Low Stock Threshold"><input type="number" value={form.output.low_stock_threshold} onChange={(e) => setForm({ ...form, output: { ...form.output, low_stock_threshold: e.target.value } })} className="input-field font-mono" data-testid="out-threshold" /></Field>
            </div>

            <div className="border border-[#28B516]/40 bg-[#28B516]/5 p-4 grid grid-cols-3 gap-4 text-xs font-mono">
              <div><div className="overline mb-1">Raw Cost (total)</div>₹{totalRawCost.toLocaleString()}</div>
              <div><div className="overline mb-1">Output Quantity</div>{form.output.quantity || 0}</div>
              <div><div className="overline mb-1">Per-Unit Cost</div><span className="text-[#28B516] text-base font-bold">₹{perUnitCost.toFixed(2)}</span></div>
            </div>

            <Field label="Notes" full><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field h-16 resize-none" data-testid="prod-notes" /></Field>

            <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs font-sans text-zinc-600 flex gap-2">
              <PackagePlus className="w-4 h-4 text-[#28B516] shrink-0 mt-0.5" />
              <span>Raw materials are <b>deducted immediately</b> on start. The finished batch will appear in <b>Ready Stock</b> only after you mark this run <b>Completed</b>.</span>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-production">Start Production</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}
